import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Container,
  IconButton,
  Pagination,
  PaginationItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import Header from "../../components/User/Header";
import { useSidebar } from "../../components/SidebarContext";
import { getApiUrl, getApiHeaders } from "../../config/api";
import { auth } from "../../config/firebaseConfig";

export const ClaimsReported = () => {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [claimToDelete, setClaimToDelete] = useState(null);

  const fetchMyClaims = async () => {
      try {
        setLoading(true);
        setError("");
        const user = auth.currentUser;
        if (!user) {
          setError("Please login to view your claim history.");
          setLoading(false);
          return;
        }
        const token = await user.getIdToken();
        const res = await fetch(getApiUrl("api/claims/my-claims"), {
          headers: getApiHeaders({ Authorization: `Bearer ${token}` })
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.detail || "Failed to fetch claims");
        }
        const list = data.claims || [];
        setClaims(list);
      } catch (e) {
        setError(e.message || "Error fetching claims");
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    fetchMyClaims();
  }, []);

  const handleDeleteClick = (claim) => {
    setClaimToDelete(claim);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!claimToDelete) return;
    
    try {
      const user = auth.currentUser;
      if (!user) {
        alert("Please login to delete claims.");
        return;
      }
      
      const token = await user.getIdToken();
      const res = await fetch(getApiUrl(`api/claims/${claimToDelete.id}`), {
        method: 'DELETE',
        headers: getApiHeaders({ Authorization: `Bearer ${token}` })
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to delete claim");
      }
      
      // Refresh the claims list
      await fetchMyClaims();
      setDeleteDialogOpen(false);
      setClaimToDelete(null);
    } catch (e) {
      alert(`Error deleting claim: ${e.message}`);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setClaimToDelete(null);
  };

  const { isSidebarOpen } = useSidebar();
  const isMobile = /Mobi|Android/i.test(navigator.userAgent) || window.innerWidth <= 600;

  return (
    <Box
      sx={{
        transition: "margin-left 0.3s cubic-bezier(0.4,0,0.2,1), max-width 0.3s cubic-bezier(0.4,0,0.2,1)",
        marginLeft: isSidebarOpen ? (isMobile ? "200px" : "300px") : 0,
        bgcolor: "#fbffe8",
        minHeight: "100vh",
        width: "100%",
        maxWidth: isSidebarOpen ? (isMobile ? "calc(100vw - 200px)" : "calc(100vw - 300px)") : "100vw",
        overflowX: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header title="Claim History" />
      <Container maxWidth="xl" sx={{ pt: { xs: 2, md: 3 }, pb: 4 }}>
        {/* Claims Table */}
        <Paper
          elevation={4}
          sx={{
            bgcolor: "#e1ecc8",
            borderRadius: "20px",
            p: { xs: 2, sm: 3, md: 4 },
            mb: 5,
          }}
        >
          <TableContainer sx={{ bgcolor: "transparent" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontFamily: "Poppins, Helvetica",
                      fontWeight: "bold",
                      color: "#516d4f",
                      fontSize: { xs: "14px", sm: "16px", md: "18px" },
                      border: "none",
                      pb: 3,
                    }}
                  >
                    Item Description
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "Poppins, Helvetica",
                      fontWeight: "bold",
                      color: "#516d4f",
                      fontSize: { xs: "14px", sm: "16px", md: "18px" },
                      border: "none",
                      pb: 3,
                    }}
                  >
                    Linked Item
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "Poppins, Helvetica",
                      fontWeight: "bold",
                      color: "#516d4f",
                      fontSize: { xs: "14px", sm: "16px", md: "18px" },
                      border: "none",
                      pb: 3,
                    }}
                  >
                    Submitted
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "Poppins, Helvetica",
                      fontWeight: "bold",
                      color: "#516d4f",
                      fontSize: { xs: "14px", sm: "16px", md: "18px" },
                      border: "none",
                      pb: 3,
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "Poppins, Helvetica",
                      fontWeight: "bold",
                      color: "#516d4f",
                      fontSize: "18px",
                      border: "none",
                      pb: 3,
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={5} sx={{ fontFamily: 'Poppins, Helvetica', color: '#516d4f', fontSize: { xs: '14px', sm: '16px', md: '18px' } }}>Loading...</TableCell></TableRow>
                ) : error ? (
                  <TableRow><TableCell colSpan={5} sx={{ fontFamily: 'Poppins, Helvetica', color: '#c74f4f', fontSize: { xs: '14px', sm: '16px', md: '18px' } }}>{error}</TableCell></TableRow>
                ) : (claims.length || 0) === 0 ? (
                  <TableRow><TableCell colSpan={5} sx={{ fontFamily: 'Poppins, Helvetica', color: '#516d4f', fontSize: { xs: '14px', sm: '16px', md: '18px' } }}>No claims yet</TableCell></TableRow>
                ) : (
                  claims.map((c) => (
                    <TableRow key={c.id} hover>
                      <TableCell sx={{ fontFamily: 'Poppins, Helvetica', color: '#516d4f', fontSize: { xs: '14px', sm: '16px', md: '18px' }, border: 'none', py: 1 }}>
                        {c.item_description}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Poppins, Helvetica', color: '#516d4f', fontSize: { xs: '14px', sm: '16px', md: '18px' }, border: 'none', py: 1 }}>
                        {c.item_name ? `${c.item_name} (${c.item_type || ''})` : c.found_item_id || '—'}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Poppins, Helvetica', color: '#516d4f', fontSize: { xs: '14px', sm: '16px', md: '18px' }, border: 'none', py: 1 }}>
                        {c.claim_date ? new Date(c.claim_date).toLocaleString() : '—'}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Poppins, Helvetica', color: '#516d4f', fontSize: { xs: '14px', sm: '16px', md: '18px' }, border: 'none', py: 1 }}>
                        {(c.status || '').toUpperCase()}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Poppins, Helvetica', border: 'none', py: 1 }}>
                        <IconButton 
                          onClick={() => handleDeleteClick(c)}
                          sx={{ 
                            color: '#c74f4f',
                            '&:hover': { 
                              bgcolor: 'rgba(199, 79, 79, 0.1)' 
                            }
                          }}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination (static placeholder) */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 10 }}>
            <Pagination
              count={1}
              renderItem={(item) => (
                <PaginationItem
                  slots={{
                    previous: ArrowBackIosNewIcon,
                    next: ArrowForwardIosIcon,
                  }}
                  {...item}
                  sx={{
                    fontFamily: "Poppins, Helvetica",
                    color: "#516d4f",
                    fontSize: "20px",
                  }}
                />
              )}
              siblingCount={0}
              boundaryCount={0}
              size="small"
              sx={{
                "& .MuiPaginationItem-root": {
                  color: "#516d4f",
                  fontFamily: "Poppins, Helvetica",
                },
                "& .MuiPaginationItem-icon": {
                  fontSize: 16,
                },
              }}
            />
            <Typography
              sx={{
                fontFamily: "Poppins, Helvetica",
                color: "#516d4f",
                fontSize: "20px",
                mx: 1,
                alignSelf: "center",
              }}
            >
              Page 1 of 1
            </Typography>
          </Box>
        </Paper>
      </Container>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 2,
          }
        }}
      >
        <DialogTitle sx={{ fontFamily: 'Poppins, Helvetica', fontWeight: 600, color: '#344b35' }}>
          Delete Claim
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: 'Poppins, Helvetica', color: '#516d4f' }}>
            Are you sure you want to delete this claim? This action cannot be undone.
            {claimToDelete && (
              <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: '8px' }}>
                <Typography variant="body2" sx={{ fontFamily: 'Poppins, Helvetica', color: '#516d4f' }}>
                  <strong>Item:</strong> {claimToDelete.item_description}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'Poppins, Helvetica', color: '#516d4f', mt: 1 }}>
                  <strong>Status:</strong> {(claimToDelete.status || '').toUpperCase()}
                </Typography>
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleDeleteCancel}
            sx={{ 
              fontFamily: 'Poppins, Helvetica',
              color: '#516d4f',
              textTransform: 'none',
              px: 3
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm}
            variant="contained"
            sx={{ 
              fontFamily: 'Poppins, Helvetica',
              bgcolor: '#c74f4f',
              textTransform: 'none',
              px: 3,
              '&:hover': {
                bgcolor: '#a63d3d'
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClaimsReported;